// src/components/ui/index.js
import React from 'react';

export const Chip = ({ label, active, onClick, color = 'rose' }) => (
  <button onClick={onClick}
    className={`flex-shrink-0 px-3 py-1 rounded-full text-[10px] font-bold font-ui border transition-all whitespace-nowrap
      ${active
        ? color === 'slate' ? 'bg-slate text-white border-slate' : 'bg-rose text-white border-rose'
        : 'bg-white text-ink-mid border-border hover:border-rose-lt hover:text-rose'
      }`}
    style={{ fontFamily:'Nunito,system-ui,sans-serif' }}>
    {label}
  </button>
);

export const SectionLabel = ({ text, action, onAction }) => (
  <div className="flex items-center justify-between px-4 pt-4 pb-1 md:px-8">
    <span className="text-[9px] font-bold font-ui text-ink-lt uppercase tracking-[1.5px]"
      style={{ fontFamily:'Nunito,sans-serif' }}>{text}</span>
    {action && (
      <button onClick={onAction} className="text-[10px] font-bold font-ui text-rose hover:opacity-80 transition-opacity"
        style={{ fontFamily:'Nunito,sans-serif' }}>{action}</button>
    )}
  </div>
);

export const Card = ({ children, className = '', onClick, style }) => (
  <div onClick={onClick} style={style}
    className={`bg-white rounded-xl border shadow-xs transition-all duration-200
      ${onClick ? 'cursor-pointer hover:shadow-sm hover:border-rose-lt' : ''}
      border-border ${className}`}>
    {children}
  </div>
);

export const ProgressBar = ({ value, color = 'rose', className = '' }) => (
  <div className={`h-0.5 rounded-full overflow-hidden ${className}`} style={{ background:'var(--border)' }}>
    <div className="h-full rounded-full transition-all duration-500"
      style={{ width:`${value}%`, background: color === 'rose' ? 'var(--rose-lt)' : 'var(--sage)' }}/>
  </div>
);

export const Toggle = ({ value, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3">
    <div className="flex-1 pr-4">
      {label && <div className="text-sm font-semibold font-ui text-ink">{label}</div>}
      {description && <div className="text-xs font-ui text-ink-lt mt-0.5">{description}</div>}
    </div>
    <button onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full flex items-center transition-all duration-300 px-0.5`}
      style={{ background: value ? 'var(--sage)' : 'var(--border)' }}>
      <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-all duration-300 ${value ? 'translate-x-5' : 'translate-x-0'}`}/>
    </button>
  </div>
);

export const Avatar = ({ initials, size = 'md' }) => {
  const s = { sm:'w-7 h-7 text-xs', md:'w-9 h-9 text-sm', lg:'w-14 h-14 text-xl' };
  return (
    <div className={`rounded-full flex items-center justify-center font-bold font-ui text-white flex-shrink-0 ${s[size]}`}
      style={{ background:'var(--rose)', fontFamily:'Nunito,sans-serif' }}>
      {initials}
    </div>
  );
};

export const NotifDot = ({ count }) => count > 0 ? (
  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
    style={{ background:'var(--rose)', fontFamily:'Nunito,sans-serif' }}>
    {count > 9 ? '9+' : count}
  </span>
) : null;

export const EmptyState = ({ icon, title, desc, action, onAction }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center animate-fade-in">
    <div className="text-5xl mb-4 opacity-30">{icon}</div>
    <div className="font-display text-lg text-ink font-bold mb-2"
      style={{ fontFamily:'Playfair Display,Georgia,serif' }}>{title}</div>
    <div className="text-sm font-ui text-ink-lt mb-6 max-w-xs">{desc}</div>
    {action && (
      <button onClick={onAction}
        className="px-5 py-2.5 text-white rounded-lg text-sm font-bold font-ui hover:opacity-90 transition-opacity"
        style={{ background:'var(--rose)', fontFamily:'Nunito,sans-serif' }}>{action}</button>
    )}
  </div>
);

export const Toast = ({ message, type = 'success', onClose }) => (
  <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up pointer-events-none">
    <div className="flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-white text-xs font-bold font-ui"
      style={{
        background: type === 'success' ? 'var(--sage)' : type === 'error' ? 'var(--rose)' : '#2C2C35',
        fontFamily:'Nunito,sans-serif'
      }}>
      <span>{type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}</span>
      <span>{message}</span>
    </div>
  </div>
);

export const Modal = ({ isOpen, onClose, title, icon = '🔒', children, actionLabel = 'Close', onAction }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden border shadow-xl flex flex-col animate-scale-up relative"
        style={{ borderColor: 'var(--border)' }} onClick={e => e.stopPropagation()}>
        
        {/* Close Button */}
        <button onClick={onClose} 
          className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full hover:bg-rose-mist text-ink-lt hover:text-rose transition-all duration-200"
          title="Close">
          ✕
        </button>

        <div className="p-8 pb-6 flex flex-col items-center text-center">
          <div className="text-5xl mb-6">{icon}</div>
          <h3 className="font-display text-xl font-bold text-ink mb-2"
            style={{ fontFamily: 'Playfair Display,Georgia,serif' }}>{title}</h3>
          <div className="text-sm font-ui text-ink-lt leading-relaxed"
            style={{ fontFamily: 'Nunito,sans-serif' }}>{children}</div>
        </div>
        <div className="p-6 pt-0 flex gap-3">
          <button onClick={onAction || onClose}
            className="flex-1 py-3.5 bg-rose text-white rounded-xl text-sm font-bold font-ui hover:opacity-90 transition-opacity"
            style={{ background: 'var(--rose)', fontFamily: 'Nunito,sans-serif' }}>
            {actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};